import type {
  EAutoCapability,
  EReasoningMode,
  ESearchMode,
  IAutoCapabilityOverride,
  IIndexedSelectionItemModel,
  IMessage,
} from "../types/chat";

export type TAutoSearchModeLabelKey =
  | "autoModel.composer.auto"
  | "input.web_search"
  | "input.deep_research"
  | "autoModel.composer.off";

export interface IAutoSearchModeOption {
  mode: ESearchMode;
  labelKey: TAutoSearchModeLabelKey;
}

export interface IGetAutoSearchModeOptionsProps {
  isWebSearchEnabled: boolean;
  isDeepResearchEnabled: boolean;
}

export interface IGetReasoningModeOptionsProps {
  isAutoSelected: boolean;
}

export interface IComposerModes {
  searchMode: ESearchMode;
  reasoningMode: EReasoningMode;
}

export interface IResolveComposerModesForModelChangeProps extends IComposerModes {
  wasAutoSelected: boolean;
  isAutoSelected: boolean;
}

export interface INormalizeComposerModesProps extends IComposerModes {
  isAutoSelected: boolean;
}

export interface IResolveSearchModeAfterSendProps {
  isAutoSelected: boolean;
  sentSearchMode: ESearchMode;
}

export type TAutoTurnSummaryMessage = Pick<
  IMessage,
  "role" | "isAutoRouted" | "autoCapabilities" | "autoReasoningMode"
>;

export type TAutoTurnCapabilityLabelKey =
  `autoModel.summary.capabilities.${EAutoCapability}`;

export type TAutoTurnSummaryLabelKey =
  | TAutoTurnCapabilityLabelKey
  | "autoModel.summary.thinking";

export interface IAutoTurnSummary {
  // NOTE: The capabilities shown, in display order.
  capabilities: EAutoCapability[];
  isThinking: boolean;
  // NOTE: One i18n key per item of the line, in display order.
  labelKeys: TAutoTurnSummaryLabelKey[];
}

export enum EAutoOverrideOptionKey {
  WITHOUT_WEB_SEARCH = "without_web_search",
  WITHOUT_DEEP_RESEARCH = "without_deep_research",
  WITH_THINKING = "with_thinking",
  WITHOUT_THINKING = "without_thinking",
  WITH_OTHER_MODEL = "with_other_model",
}

export type TAutoOverrideLabelKey =
  `autoModel.override.options.${EAutoOverrideOptionKey}`;

export interface IAutoOverrideOption {
  key: EAutoOverrideOptionKey;
  labelKey: TAutoOverrideLabelKey;
  // NOTE: The explicit composer value the re-run sends and keeps for the chat;
  // absent = keep the composer's current value.
  searchMode?: ESearchMode;
  reasoningMode?: EReasoningMode;
  // NOTE: The capabilities Auto enabled that the re-run drops (counted by the
  // server as overrides).
  withoutCapabilities: EAutoCapability[];
  // NOTE: Opens the model picker; the turn re-runs on the model picked there.
  isModelChange: boolean;
}

export interface IBuildRerunOverrideProps {
  messageUuid: string;
  option: IAutoOverrideOption;
}

export interface IBuildComposerDisableOverrideProps {
  isAutoSelected: boolean;
  // NOTE: The search mode the chat's previous turn was sent with.
  lastSentSearchMode: ESearchMode | null;
  searchModeForSend: ESearchMode;
  // NOTE: The chat's latest answer, when Auto routed it.
  latestAutoAnswer: (TAutoTurnSummaryMessage & { uuid: string }) | null;
}

export interface IFindLatestAutoAnswerProps<
  TMessage extends TAutoTurnSummaryMessage,
> {
  // NOTE: Oldest first, as both chat screens render them.
  messages: TMessage[];
}

// NOTE: NSAI-6541 — the contract of the clients' Auto composer hook
// (use-auto-composer.ts on web and mobile).
export interface IUseAutoComposerProps {
  models: { uuid: string; isRouterModel?: boolean }[];
  selectedModelSelections: IIndexedSelectionItemModel[];
  chatId: string | undefined;
  // NOTE: The search mode stored on the chat (its previous turn's); undefined
  // until the chat has loaded.
  chatSearchMode: ESearchMode | undefined;
  // NOTE: Oldest first.
  messages: IMessage[];
  searchMode: ESearchMode;
  reasoningMode: EReasoningMode;
  setSearchMode: (mode: ESearchMode) => void;
  setReasoningMode: (mode: EReasoningMode) => void;
}

export interface IUseAutoComposerApplyModelChangeProps {
  nextModels: IIndexedSelectionItemModel[];
}

export interface IUseAutoComposerBuildOverrideProps {
  searchModeForSend: ESearchMode;
}

export interface IUseAutoComposerRecordSendProps {
  sentSearchMode: ESearchMode;
}

export interface IUseAutoComposerRequestRerunProps {
  messageUuid: string;
}

export interface IUseAutoComposerResult {
  isAutoSelected: boolean;
  // NOTE: The chat's latest answer when Auto routed it — the one answer the
  // one-click overrides are offered on.
  latestAutoAnswerUuid: string | null;
  // NOTE: The composer's modes as they may be sent ("Auto decides" only while
  // Auto is selected).
  normalizedModes: IComposerModes;
  // NOTE: Call on a user-made model change; returns the modes it set.
  applyModelChange: (
    props: IUseAutoComposerApplyModelChangeProps,
  ) => IComposerModes;
  buildSendAutoOverride: (
    props: IUseAutoComposerBuildOverrideProps,
  ) => IAutoCapabilityOverride | null;
  // NOTE: Call after a send; returns the search mode the composer keeps.
  recordSend: (props: IUseAutoComposerRecordSendProps) => ESearchMode;
  requestModelRerun: (props: IUseAutoComposerRequestRerunProps) => void;
  // NOTE: The answer a pending "with a different model" re-run targets, once.
  consumeModelRerun: () => string | null;
  // NOTE: Drops a pending re-run; called when the picker closes without a pick.
  cancelModelRerun: () => void;
}
